import React from 'react';
import { Box, Button, Typography } from '@mui/material';
import { Bolt as EventsIcon } from '@mui/icons-material';
import DetailSidebar from '../../../../shared/components/DetailSidebar';
import DetailRow from '../../../../shared/components/DetailRow';
import { eventTimeIso } from '../../utils/datasetRowsUtils';
import type { ColumnType } from '../../utils/datasetFormUtils';
import type { RowEventTarget } from '../../../../types/frontend';

interface DatasetRowDetailPanelProps {
  /** Every column of the row, in table order. */
  columns: string[];
  types: Map<string, ColumnType>;
  values: Record<string, string | null>;
  onClose: () => void;
  /** Given when the caller can open the row's event in the Event Explorer. */
  onOpenEvent?: (target: RowEventTarget) => void;
}

function fullValue(value: string, type: ColumnType | undefined): React.ReactNode {
  if (type !== 'list' && type !== 'json') return value;
  let shown = value;
  try {
    shown = JSON.stringify(JSON.parse(value), null, 2);
  } catch {
    // A value that is not JSON is shown as Athena gave it.
  }
  // A span, since DetailRow sets its value inside a paragraph.
  return <Box component="span" sx={{ display: 'block', fontFamily: 'monospace', whiteSpace: 'pre-wrap' }}>{shown}</Box>;
}

/** One dataset row with every column in full, list and json values included, and its source event. */
const DatasetRowDetailPanel: React.FC<DatasetRowDetailPanelProps> = ({ columns, types, values, onClose, onOpenEvent }) => {
  const eventId = values.event_id;
  const eventTime = eventTimeIso(values.event_time);
  return (
    <DetailSidebar title="Row" onClose={onClose}>
      <Box sx={{ p: 2 }} data-testid="dataset-row-detail">
        {columns.map((column) => (
          <DetailRow
            key={column}
            label={column}
            labelWidth={140}
            mono={types.get(column) !== 'text'}
            value={values[column] != null
              ? fullValue(values[column], types.get(column))
              : <Typography component="span" color="text.disabled" fontSize={12.5}>empty</Typography>}
          />
        ))}
        {onOpenEvent && (
          <Button
            size="small"
            variant="outlined"
            startIcon={<EventsIcon />}
            sx={{ mt: 2 }}
            // Without an id and a readable time there is no event to find in the Explorer.
            disabled={!eventId || !eventTime}
            onClick={() => eventId && eventTime && onOpenEvent({ id: eventId, time: eventTime })}
          >
            Open in Event Explorer
          </Button>
        )}
      </Box>
    </DetailSidebar>
  );
};

export default DatasetRowDetailPanel;
